import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useRevalidator, useSearchParams } from "react-router-dom";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Container,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Stack,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { MdAdd, MdDelete, MdEdit, MdRefresh } from "react-icons/md";
import SearchBar from "../components/base/SearchBar";
import { QuestWithId } from "../models/api/quest";
import { useLoaderData } from "react-router";
import QuestList from "../components/QuestList";
import { BiCog, BiCollapseAlt, BiExpandAlt } from "react-icons/bi";
import { GoRepo, GoRepoTemplate } from "react-icons/go";
import { GrAlert, GrOverview } from "react-icons/gr";
import { AnimatePresence, motion } from "framer-motion";
import ConfirmModal from "../components/modals/ConfirmModal";
import { ulid } from "ulid";
import { QuestEditorFolder, QuestEditorFolderWithId } from "@worldwidewebb/client-quests";
import { TbFolderPlus } from "react-icons/tb";
import { useForm } from "react-hook-form";
import useQuestFolders from "../hooks/quests/useQuestFolders";
import useCreateQuestFolder from "../hooks/quests/useCreateQuestFolder";
import useUpdateQuestFolder from "../hooks/quests/useUpdateQuestFolder";
import useDeleteQuestFolder from "../hooks/quests/useDeleteQuestFolder";
import useSessionProfile from "../hooks/users/useSessionProfile";
import useDeleteQuest from "../hooks/quests/useDeleteQuest";
import useQuests from "../hooks/quests/useQuests";
import useCreateQuest from "../hooks/quests/useCreateQuest";
import useUpdateQuestMetaData from "../hooks/quests/useUpdateQuestMetaData";
import useGenerateVoices from "../hooks/voices/useGenerateVoices";
import useDuplicateQuest from "../hooks/quests/useDuplicateQuest";
import SelectSomething from "../components/base/SelectSomething";
import { useQuestsSelectionProvider } from "../context/quests/QuestsSelectionProvider";
import useUpdateQuestMetaDatas from "../hooks/quests/useUpdateQuestMetaDatas";
import QuestsResetButton from "../components/quests/QuestsResetButton";
import useSessionUserId from "../hooks/users/useSessionUserId";
import DownloadTranslatedSpeakerPassagesButton from "../features/quests/DownloadTranslatedSpeakerPassagesButton";
import DownloadTranslatedChoicesButton from "../features/quests/DownloadTranslatedChoicesButton";

function QuestsArchiveButton() {
  const { selectedQuestIds, clearSelection } = useQuestsSelectionProvider();

  const { isUpdating, updateQuestMetaDatas } = useUpdateQuestMetaDatas();

  const archiveQuests = useCallback(() => {
    updateQuestMetaDatas(
      selectedQuestIds.map((selectedQuestId) => ({
        questId: selectedQuestId,
        isArchived: true,
        isReady: false,
        status: "draft",
      })),
      {
        onSuccess: () => clearSelection(),
      }
    );
  }, [updateQuestMetaDatas, selectedQuestIds]);

  if (selectedQuestIds.length === 0) {
    return null;
  }

  return (
    <ConfirmModal
      title={`ARCHIVE QUESTS (${selectedQuestIds.length})`}
      tooltip={"archives quests after confirmation"}
      color={"red.800"}
      variant={"outline"}
      onConfirm={archiveQuests}
      isLoading={isUpdating}
      isDisabled={isUpdating}
    >
      <Text color={"white"} fontWeight={500}>
        Are you sure you want to archive {selectedQuestIds.length} quest(s)
      </Text>
    </ConfirmModal>
  );
}

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function CreateFolderModal({ isOpen, onClose }: CreateFolderModalProps) {
  const { register, handleSubmit } = useForm<Pick<QuestEditorFolder, "displayName" | "description">>({
    defaultValues: useMemo(
      () => ({
        displayName: "",
        description: "",
      }),
      []
    ),
    mode: "onChange",
  });

  const { createQuestFolder } = useCreateQuestFolder();

  const handleCreate = useCallback(
    ({ displayName, description }: Pick<QuestEditorFolder, "displayName" | "description">) => {
      createQuestFolder(
        { questEditorFolderId: ulid(), displayName, description },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    },
    [createQuestFolder, onClose]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />

      <ModalContent>
        <form onSubmit={handleSubmit(handleCreate)}>
          <ModalHeader bg={"indigo.600"}>
            <Heading size={"md"}>
              <Text color={"white"}>Create Folder</Text>
            </Heading>
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Stack>
              <FormControl>
                <FormLabel>
                  <Text color={"white"}>Name</Text>
                </FormLabel>

                <Input color={"white"} {...register("displayName")} />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <Text color={"white"}>Description</Text>
                </FormLabel>

                <Input color={"white"} {...register("description")} />
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button onClick={onClose} variant={"outline"}>
              Cancel
            </Button>
            <Button variant={"outline"} type={"submit"}>
              Create
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

interface UpdateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  questEditorFolder: QuestEditorFolderWithId;
}

function UpdateFolderModal({ isOpen, onClose, questEditorFolder }: UpdateFolderModalProps) {
  const { register, handleSubmit } = useForm<Pick<QuestEditorFolder, "displayName" | "description">>({
    defaultValues: useMemo(
      () => ({
        displayName: questEditorFolder.displayName,
        description: questEditorFolder.description,
      }),
      [questEditorFolder]
    ),
    mode: "onChange",
  });

  const { updateQuestFolder } = useUpdateQuestFolder();

  const handleUpdate = useCallback(
    ({ displayName, description }: Pick<QuestEditorFolder, "displayName" | "description">) => {
      updateQuestFolder(
        { questEditorFolderId: questEditorFolder.questEditorFolderId, questEditorFolder: { displayName, description } },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    },
    [updateQuestFolder, onClose]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />

      <ModalContent>
        <form onSubmit={handleSubmit(handleUpdate)}>
          <ModalHeader bg={"indigo.600"}>
            <Heading size={"md"}>
              <Text color={"white"}>Update Folder</Text>
            </Heading>
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Stack>
              <FormControl>
                <FormLabel>
                  <Text color={"white"}>Name</Text>
                </FormLabel>

                <Input color={"white"} {...register("displayName")} />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <Text color={"white"}>Description</Text>
                </FormLabel>

                <Input color={"white"} {...register("description")} />
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button onClick={onClose} variant={"outline"}>
              Cancel
            </Button>
            <Button variant={"outline"} type={"submit"}>
              Update
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

interface DeleteFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  questEditorFolder: QuestEditorFolderWithId;
}

function DeleteFolderModal({ isOpen, onClose, questEditorFolder: { questEditorFolderId } }: DeleteFolderModalProps) {
  const leastDestructiveRef = useRef<HTMLButtonElement>(null);

  const { deleteQuestFolder } = useDeleteQuestFolder();

  const handleDelete = useCallback(
    (questEditorFolderId: string) => {
      deleteQuestFolder(questEditorFolderId, {
        onSuccess: () => {
          onClose();
        },
      });
    },
    [deleteQuestFolder, onClose]
  );

  return (
    <AlertDialog isOpen={isOpen} onClose={onClose} leastDestructiveRef={leastDestructiveRef}>
      <AlertDialogOverlay />

      <AlertDialogContent>
        <AlertDialogHeader bg={"indigo.600"}>
          <Heading size={"md"}>
            <Text color={"white"}>Delete Folder</Text>
          </Heading>
        </AlertDialogHeader>

        <AlertDialogCloseButton />

        <AlertDialogBody>
          <Stack>
            <Text color={"white"}>Are you sure you want to delete the folder? Quests will not be deleted.</Text>
          </Stack>
        </AlertDialogBody>

        <AlertDialogFooter gap={2}>
          <Button onClick={onClose} variant={"outline"} ref={leastDestructiveRef}>
            Cancel
          </Button>
          <Button variant={"outline"} onClick={() => handleDelete(questEditorFolderId)} color={"red.800"}>
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface QuestFoldersProps {
  sortByCreatedAt: SortBy;
  isSearching: boolean;
  questEditorFolders: QuestEditorFolderWithId[];
  quests: QuestWithId[];
  filteredQuests: QuestWithId[];
  onUpdate: (quest: QuestWithId) => void;
  isUpdating: boolean;
  onDelete: (questId: string) => void;
  isDeleting: boolean;
  onDuplicate: (questId: string) => void;
  isDuplicating: boolean;
}

function QuestFolders({
  sortByCreatedAt,
  isSearching,
  questEditorFolders,
  quests,
  filteredQuests,
  onUpdate,
  isUpdating,
  onDelete,
  isDeleting,
  onDuplicate,
  isDuplicating,
}: QuestFoldersProps) {
  const questEditorFoldersWithQuests = useMemo(
    () =>
      questEditorFolders
        .map((questEditorFolder) => ({
          questEditorFolder,
          quests: quests.filter(({ folderId }) => folderId === questEditorFolder.questEditorFolderId),
          filteredQuests: filteredQuests.filter(({ folderId }) => folderId === questEditorFolder.questEditorFolderId),
        }))
        .sort(({ questEditorFolder: a }, { questEditorFolder: b }) => {
          if (sortByCreatedAt === "none") {
            return a.displayName.localeCompare(b.displayName);
          }

          const aTime = new Date(a.createdAt ?? new Date()).getTime();
          const bTime = new Date(b.createdAt ?? new Date()).getTime();

          return sortByCreatedAt === "ascending" ? aTime - bTime : bTime - aTime;
        }),
    [questEditorFolders, filteredQuests, sortByCreatedAt]
  );

  const [openIndexes, setOpenIndexes] = useState<number[]>([]);

  const handleToggleOpenIndex = useCallback(
    (index: number) => {
      if (openIndexes.includes(index)) {
        setOpenIndexes((openIndexes) => openIndexes.filter((openIndex) => openIndex !== index));
      } else {
        setOpenIndexes((openIndexes) => openIndexes.concat(index));
      }
    },
    [openIndexes]
  );

  const handleCollapseFolders = useCallback(() => {
    setOpenIndexes([]);
  }, [questEditorFoldersWithQuests]);

  const handleExpandFolders = useCallback(() => {
    setOpenIndexes(Array.from({ length: questEditorFoldersWithQuests.length }, (_, index) => index));
  }, [questEditorFoldersWithQuests]);

  useEffect(() => {
    if (isSearching) {
      handleExpandFolders();
    } else {
      handleCollapseFolders();
    }
  }, [isSearching, handleExpandFolders, handleCollapseFolders]);

  return (
    <Accordion allowMultiple index={openIndexes}>
      {questEditorFoldersWithQuests
        .filter(({ filteredQuests }) => (isSearching ? filteredQuests.length !== 0 : true))
        .map(({ questEditorFolder, quests, filteredQuests }, index) => (
          <QuestFolder
            key={questEditorFolder.questEditorFolderId}
            index={index}
            onToggleOpenIndex={handleToggleOpenIndex}
            isSearching={isSearching}
            questEditorFolder={questEditorFolder}
            quests={quests}
            filteredQuests={filteredQuests}
            onUpdate={onUpdate}
            isUpdating={isUpdating}
            onDelete={onDelete}
            isDeleting={isDeleting}
            onDuplicate={onDuplicate}
            isDuplicating={isDuplicating}
          />
        ))}
    </Accordion>
  );
}

interface QuestFolderProps {
  index: number;
  onToggleOpenIndex: (index: number) => void;
  isSearching: boolean;
  questEditorFolder: QuestEditorFolderWithId;
  quests: QuestWithId[];
  filteredQuests: QuestWithId[];
  onUpdate: (quest: QuestWithId) => void;
  isUpdating: boolean;
  onDelete: (questId: string) => void;
  isDeleting: boolean;
  onDuplicate: (questId: string) => void;
  isDuplicating: boolean;
}

function QuestFolder({
  index,
  onToggleOpenIndex,
  isSearching,
  questEditorFolder,
  quests,
  filteredQuests,
  onUpdate,
  isUpdating,
  onDelete,
  isDeleting,
  onDuplicate,
  isDuplicating,
}: QuestFolderProps) {
  const { displayName, description } = questEditorFolder;

  const { isOpen: isOpenUpdateFolder, onOpen: onOpenUpdateFolder, onClose: onCloseUpdateFolder } = useDisclosure();
  const { isOpen: isOpenDeleteFolder, onOpen: onOpenDeleteFolder, onClose: onCloseDeleteFolder } = useDisclosure();
  const { revalidate } = useRevalidator();

  const [openIndexes, setOpenIndexes] = useState<number[]>([]);

  const handleCollapseQuests = useCallback(() => {
    setOpenIndexes([]);
  }, [filteredQuests]);

  const handleExpandQuests = useCallback(() => {
    setOpenIndexes(Array.from({ length: filteredQuests.length }, (_, index) => index));
  }, [filteredQuests]);

  useEffect(() => {
    if (isSearching) {
      handleExpandQuests();
    } else {
      handleCollapseQuests();
    }
  }, [isSearching, handleExpandQuests, handleCollapseQuests]);

  const handleToggleOpenIndex = useCallback(
    (index: number) => {
      if (openIndexes.includes(index)) {
        setOpenIndexes((openIndexes) => openIndexes.filter((openIndex) => openIndex !== index));
      } else {
        setOpenIndexes((openIndexes) => openIndexes.concat(index));
      }
    },
    [openIndexes]
  );

  const [isActivating, setIsActivating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  useEffect(() => {
    if (isActivating && quests.some(({ isReady }) => !isReady)) {
      return;
    }

    setIsActivating(false);
  }, [quests, isActivating]);

  useEffect(() => {
    if (isDeactivating && quests.some(({ isReady }) => isReady)) {
      return;
    }

    setIsDeactivating(false);
  }, [quests, isDeactivating]);

  const handleOnOpenUpdateFolder = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      onOpenUpdateFolder();
    },
    [onOpenUpdateFolder]
  );

  const handleOnOpenDeleteFolder = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      onOpenDeleteFolder();
    },
    [onOpenDeleteFolder]
  );

  const handleActivateQuests = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      setIsActivating(true);

      quests.filter(({ isReady }) => !isReady).forEach((quest) => onUpdate({ ...quest, isReady: true }));
    },
    [quests, onUpdate]
  );

  const handleDeactivateQuests = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      setIsDeactivating(true);

      quests.filter(({ isReady }) => isReady).forEach((quest) => onUpdate({ ...quest, isReady: false }));
    },
    [quests, onUpdate]
  );

  const readyQuestCount = quests.filter(({ isReady }) => isReady).length;
  const totalQuestCount = quests.length;

  let colorQuestCount = "orange.600";

  if (readyQuestCount === 0) {
    colorQuestCount = "red.800";
  }

  if (readyQuestCount === totalQuestCount) {
    colorQuestCount = "green.600";
  }

  const { sessionUserId } = useSessionUserId();
  const questIds = useMemo(() => quests.map(({ questId }) => questId), [quests]);

  return (
    <>
      <AccordionItem>
        <AccordionButton justifyContent={"space-between"} p={1} onClick={() => onToggleOpenIndex(index)}>
          <Stack textAlign={"start"}>
            <Heading size={"md"}>
              <Text color={"white"} casing={"uppercase"}>
                {displayName}
              </Text>
            </Heading>

            <Text color={"white"} casing={"uppercase"} size={"sm"}>
              {description}
            </Text>

            <HStack>
              <Tooltip label={"update folder"}>
                <IconButton
                  as={"div"}
                  aria-label={"update folder"}
                  icon={<Icon position={"absolute"} as={MdEdit} />}
                  onClick={handleOnOpenUpdateFolder}
                  isDisabled={isActivating || isDeactivating}
                  color={"white"}
                />
              </Tooltip>
              <Tooltip label={"delete folder"}>
                <IconButton
                  as={"div"}
                  aria-label={"delete folder"}
                  icon={<Icon position={"absolute"} as={MdDelete} />}
                  onClick={handleOnOpenDeleteFolder}
                  isDisabled={isActivating || isDeactivating}
                  color={"red.800"}
                />
              </Tooltip>

              {sessionUserId && <QuestsResetButton questIds={questIds} userId={sessionUserId} />}

              <Tag h={10} bg={"whiteAlpha.200"}>
                <Text color={colorQuestCount} casing={"uppercase"}>
                  {readyQuestCount}/{totalQuestCount} active
                </Text>
              </Tag>
            </HStack>
          </Stack>

          <AccordionIcon />
        </AccordionButton>

        <AccordionPanel p={1}>
          <Stack>
            <HStack>
              <Tooltip label={"expand quests"}>
                <IconButton
                  color={"white"}
                  variant={"outline"}
                  icon={<Icon position={"absolute"} as={BiExpandAlt} />}
                  aria-label={"expand quests"}
                  onClick={handleExpandQuests}
                />
              </Tooltip>
              <Tooltip label={"collapse quests"}>
                <IconButton
                  color={"white"}
                  variant={"outline"}
                  icon={<Icon position={"absolute"} as={BiCollapseAlt} />}
                  aria-label={"collapse quests"}
                  onClick={handleCollapseQuests}
                />
              </Tooltip>
            </HStack>

            <QuestList
              flexGrow={1}
              quests={filteredQuests}
              onUpdate={onUpdate}
              isUpdating={isUpdating}
              onDelete={onDelete}
              isDeleting={isDeleting}
              onDuplicate={onDuplicate}
              isDuplicating={isDuplicating}
              openIndexes={openIndexes}
              onToggleOpenIndex={handleToggleOpenIndex}
            />
          </Stack>
        </AccordionPanel>
      </AccordionItem>

      {isOpenUpdateFolder && (
        <UpdateFolderModal
          isOpen={isOpenUpdateFolder}
          onClose={onCloseUpdateFolder}
          questEditorFolder={questEditorFolder}
        />
      )}

      {isOpenDeleteFolder && (
        <DeleteFolderModal
          isOpen={isOpenDeleteFolder}
          onClose={onCloseDeleteFolder}
          questEditorFolder={questEditorFolder}
        />
      )}
    </>
  );
}

type SortBy = "none" | "ascending" | "descending";

const sortByTypes: Record<SortBy, SortBy> = {
  none: "none",
  ascending: "ascending",
  descending: "descending",
};

const Quests: React.FC = () => {
  const [sortByCreatedAt, setSortByCreatedAt] = useState<SortBy>("descending");

  const { isCreating, createQuest } = useCreateQuest();
  const { isDeleting, deleteQuest } = useDeleteQuest();
  const { isUpdating, updateQuestMetaData } = useUpdateQuestMetaData();
  const { isDuplicating, duplicateQuest } = useDuplicateQuest();

  const [searchParams, setSearchParams] = useSearchParams();
  const showTemplates = Boolean(JSON.parse(searchParams.get("showTemplates") ?? "false"));
  const showIsArchived = Boolean(JSON.parse(searchParams.get("showArchived") ?? "false"));

  const { isLoading, quests = [] } = useQuests(showIsArchived);
  const { questFolders = [] } = useQuestFolders();

  const [showManagementTools, setShowManagementTools] = useState<boolean>(false);

  const [isReloadingQuests, setIsReloadingQuests] = useState<boolean>(false);

  const [searchValue, setSearchValue] = useState<string>("");

  const questsByIsTemplate = useMemo(
    () =>
      quests
        .filter(({ isTemplate }) => showTemplates === isTemplate)
        .sort((a, b) => {
          if (sortByCreatedAt === "none") {
            return a.name.localeCompare(b.name);
          }

          const aTime = new Date(a.createdAt ?? new Date()).getTime();
          const bTime = new Date(b.createdAt ?? new Date()).getTime();

          return sortByCreatedAt === "ascending" ? aTime - bTime : bTime - aTime;
        }),
    [quests, showTemplates, sortByCreatedAt]
  );

  const filteredQuests = useMemo(
    () => questsByIsTemplate.filter(({ name }) => name.toLowerCase().includes(searchValue.toLowerCase().trim())),
    [questsByIsTemplate, searchValue]
  );

  const orphanFilteredQuests = useMemo(
    () =>
      filteredQuests.filter(
        ({ folderId = "" }) => !questFolders.map(({ questEditorFolderId }) => questEditorFolderId).includes(folderId)
      ),
    [filteredQuests, questFolders]
  );

  const filteredQuestCount = filteredQuests.length;

  const toast = useToast();
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();

  const handleReloadQuests = useCallback(async () => {
    try {
      setIsReloadingQuests(true);

      revalidate();

      toast({
        title: "Reloaded quests",
        status: "success",
      });
    } catch (error) {
      console.error(error);

      toast({
        title: "Error occurred reloading quests",
        description: (error as Error).message,
        status: "error",
      });
    } finally {
      setIsReloadingQuests(false);
    }
  }, []);

  const { sessionProfile } = useSessionProfile();
  const sessionProfileUserDisplayName = sessionProfile?.displayName;

  const handleCreateQuest = useCallback(() => {
    const userDisplayName = sessionProfileUserDisplayName ?? "";
    const userCreatedDate = new Date();
    const displayName = `${userDisplayName.toUpperCase()} - ${userCreatedDate.toUTCString()}`;

    createQuest(displayName, {
      onSuccess: ({ questId }) => {
        navigate(questId);
      },
    });
  }, [createQuest, sessionProfileUserDisplayName]);

  const handleUpdateQuest = useCallback(
    (quest: QuestWithId) => {
      updateQuestMetaData({ questId: quest.questId, partialQuest: quest });
    },
    [updateQuestMetaData]
  );

  const handleDeleteQuest = useCallback(
    async (questId: string) => {
      deleteQuest(questId);
    },
    [deleteQuest]
  );

  const handleDuplicateQuest = useCallback(
    async (questId: string) => {
      duplicateQuest(questId);
    },
    [duplicateQuest]
  );

  const [openIndexes, setOpenIndexes] = useState<number[]>([]);

  const handleCollapseQuests = useCallback(() => {
    setOpenIndexes([]);
  }, [filteredQuests]);

  const handleExpandQuests = useCallback(() => {
    setOpenIndexes(Array.from({ length: filteredQuests.length }, (_, index) => index));
  }, [filteredQuests]);

  const handleToggleOpenIndex = useCallback(
    (index: number) => {
      if (openIndexes.includes(index)) {
        setOpenIndexes((openIndexes) => openIndexes.filter((openIndex) => openIndex !== index));
      } else {
        setOpenIndexes((openIndexes) => openIndexes.concat(index));
      }
    },
    [openIndexes]
  );

  const { generateVoices } = useGenerateVoices();
  const [isRegeneratingVoiceAudio, setIsRegeneratingVoiceAudio] = useState(false);
  const [regeneratingVoiceAudioProgress, setRegeneratingVoiceAudioProgress] = useState(0);

  const handleRegenerateVoiceAudio = useCallback(async () => {
    setIsRegeneratingVoiceAudio(true);
    setRegeneratingVoiceAudioProgress(0);

    await Promise.all(
      questsByIsTemplate.map(async ({ questId }) => {
        generateVoices(questId, {
          onSuccess: () => {
            setRegeneratingVoiceAudioProgress((value) => value + 1);
          },
        });
      })
    );

    toast({
      title: "Requested regeneration of voice audio",
      status: "success",
    });

    setIsRegeneratingVoiceAudio(false);
  }, [questsByIsTemplate, generateVoices]);

  const [isNavigating, setIsNavigating] = useState<boolean>(false);

  const navigateWithLoading = useCallback(
    (url: string) => {
      setIsNavigating(true);

      navigate(url);
    },
    [navigate]
  );

  const handleToggleShowTemplates = useCallback(() => {
    setSearchParams({ showTemplates: String(!showTemplates) });

    setIsNavigating(true);
  }, [showTemplates]);

  const handleToggleShowArchived = useCallback(() => {
    setSearchParams({ showArchived: String(!showIsArchived) });

    setIsNavigating(true);
  }, [showIsArchived]);

  useEffect(() => {
    setIsNavigating(false);
  }, [showTemplates, showIsArchived]);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const isSearching = searchValue !== "";

  useEffect(() => {
    if (isSearching) {
      handleExpandQuests();
    } else {
      handleCollapseQuests();
    }
  }, [isSearching, handleExpandQuests, handleCollapseQuests]);

  return (
    <Container maxW={"4xl"} display={"flex"}>
      <Card bg={"theme.dark.background"} borderRadius={0} flexGrow={1}>
        <CardHeader bg={"theme.dark.background"} borderBottomWidth={2} position={"sticky"} top={0} zIndex={1}>
          <Stack>
            <HStack borderColor={"indigo.600"} borderBottomWidth={2} justifyContent={"space-between"}>
              <HStack>
                <Tooltip label={showTemplates ? "show quests" : "show quest templates"}>
                  <IconButton
                    bg={"indigo.600"}
                    variant={"outline"}
                    borderRadius={0}
                    borderBottomWidth={0}
                    isLoading={isNavigating}
                    onClick={() => handleToggleShowTemplates()}
                    aria-label={showTemplates ? "show quests" : "show quest templates"}
                    icon={<Icon as={showTemplates ? GoRepoTemplate : GoRepo} />}
                  />
                </Tooltip>
                <Tooltip label={"show quests overview"}>
                  <IconButton
                    bg={"indigo.600"}
                    variant={"outline"}
                    borderRadius={0}
                    borderBottomWidth={0}
                    isLoading={isNavigating}
                    aria-label={"show quests overview"}
                    onClick={() => navigateWithLoading("/quests-overview")}
                    icon={<Icon as={GrOverview} />}
                  />
                </Tooltip>
                <Tooltip label={"show quest issues"}>
                  <Link aria-label={"Open Quest"} color={"white"} to={"/quest-issues"} target={"_blank"}>
                    <Box px={4} py={2} bg={"red.800"}>
                      <Icon as={GrAlert} />
                    </Box>
                  </Link>
                </Tooltip>
              </HStack>

              <Button
                bg={"indigo.600"}
                variant={"outline"}
                borderRadius={0}
                borderBottomWidth={0}
                isLoading={isLoading}
                onClick={() => handleToggleShowArchived()}
              >
                <Text color={"white"} casing={"uppercase"}>
                  {showIsArchived ? "View Quests" : "View Archive"}
                </Text>
              </Button>
            </HStack>

            <HStack>
              <SearchBar
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                inputFocusKey={"/"}
                placeholder={"Search for quests"}
              />

              <Tooltip label={"reload quests"}>
                <IconButton
                  color={"white"}
                  variant={"outline"}
                  icon={<Icon as={MdRefresh} />}
                  aria-label={"reload quests"}
                  isLoading={isReloadingQuests}
                  onClick={handleReloadQuests}
                />
              </Tooltip>
              <Tooltip label={"create quest"}>
                <IconButton
                  color={"white"}
                  variant={"outline"}
                  icon={<Icon as={MdAdd} />}
                  aria-label={"create quest"}
                  isLoading={isCreating}
                  onClick={handleCreateQuest}
                />
              </Tooltip>
            </HStack>

            <HStack flexGrow={1} justifyContent={"space-between"} pb={2}>
              <HStack>
                <Tooltip label={"expand quests"}>
                  <IconButton
                    color={"white"}
                    variant={"outline"}
                    icon={<Icon as={BiExpandAlt} />}
                    aria-label={"expand quests"}
                    onClick={handleExpandQuests}
                  />
                </Tooltip>
                <Tooltip label={"collapse quests"}>
                  <IconButton
                    color={"white"}
                    variant={"outline"}
                    icon={<Icon as={BiCollapseAlt} />}
                    aria-label={"collapse quests"}
                    onClick={handleCollapseQuests}
                  />
                </Tooltip>

                <QuestsArchiveButton />
              </HStack>

              <HStack>
                <Tooltip label={"management tools"}>
                  <IconButton
                    color={"white"}
                    variant={showManagementTools ? "solid" : "outline"}
                    icon={<Icon as={BiCog} />}
                    aria-label={"management tools"}
                    onClick={() => setShowManagementTools((showManagementTools) => !showManagementTools)}
                  />
                </Tooltip>
              </HStack>
            </HStack>

            <HStack flexGrow={1} pb={2}>
              <HStack>
                <SelectSomething
                  title="Sort By Created At"
                  color={"white"}
                  values={sortByTypes}
                  value={sortByCreatedAt}
                  setValue={(value) => setSortByCreatedAt(value as SortBy)}
                />
              </HStack>
            </HStack>
          </Stack>

          <AnimatePresence>
            {showManagementTools && (
              <motion.div
                variants={{
                  hide: { transition: { height: { delay: 0.25 } }, opacity: 0, height: 0 },
                  show: { transition: { opacity: { delay: 0.25 } }, opacity: 1, height: "auto" },
                }}
                initial={"hide"}
                animate={"show"}
                exit={"hide"}
                transition={{ ease: "easeInOut", duration: 0.5 }}
              >
                <Stack borderWidth={1} borderRadius={6} p={2}>
                  <ConfirmModal color={"white"} title={"Regenerate voice audio"} onConfirm={handleRegenerateVoiceAudio}>
                    Regenerating voice audio will trigger a batch process for all quests, continue?
                  </ConfirmModal>

                  {isRegeneratingVoiceAudio && (
                    <Progress
                      colorScheme={"whiteAlpha"}
                      min={0}
                      max={questsByIsTemplate.length}
                      value={regeneratingVoiceAudioProgress}
                    />
                  )}

                  <DownloadTranslatedSpeakerPassagesButton />
                  <DownloadTranslatedChoicesButton />
                </Stack>
              </motion.div>
            )}
          </AnimatePresence>
        </CardHeader>

        <CardBody display={"flex"}>
          <Stack flexGrow={1}>
            <HStack>
              <Tooltip label={"create folder"}>
                <IconButton
                  color={"white"}
                  aria-label={"create folder"}
                  icon={<Icon as={TbFolderPlus} onClick={onOpen} />}
                />
              </Tooltip>
            </HStack>

            <QuestFolders
              sortByCreatedAt={sortByCreatedAt}
              isSearching={isSearching}
              questEditorFolders={questFolders}
              quests={questsByIsTemplate}
              filteredQuests={filteredQuests}
              onUpdate={handleUpdateQuest}
              isUpdating={isUpdating}
              onDelete={handleDeleteQuest}
              isDeleting={isDeleting}
              onDuplicate={handleDuplicateQuest}
              isDuplicating={isDuplicating}
            />

            {orphanFilteredQuests.length !== 0 && (
              <QuestList
                flexGrow={1}
                quests={orphanFilteredQuests}
                onUpdate={handleUpdateQuest}
                isUpdating={isUpdating}
                onDelete={handleDeleteQuest}
                isDeleting={isDeleting}
                onDuplicate={handleDuplicateQuest}
                isDuplicating={isDuplicating}
                openIndexes={openIndexes}
                onToggleOpenIndex={handleToggleOpenIndex}
              />
            )}
          </Stack>

          {isOpen && <CreateFolderModal isOpen={isOpen} onClose={onClose} />}
        </CardBody>

        <CardFooter bg={"theme.dark.background"} borderTopWidth={2} position={"sticky"} bottom={0}>
          <Text color={"white"} casing={"uppercase"} fontWeight={500}>
            Showing {filteredQuestCount} quest{filteredQuestCount !== 1 ? "s" : ""}
          </Text>
        </CardFooter>
      </Card>
    </Container>
  );
};

export default Quests;
